import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { 
  ArrowLeft,
  Printer,
  Download,
  Mail, 
  Phone, 
  Globe, 
  MapPin,
  Building2,
  Briefcase,
  Camera,
  Linkedin,
  Twitter,
  Instagram,
  Github,
  Facebook,
  RectangleHorizontal,
  RectangleVertical,
  Palette
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = window.location.origin;

// Color presets for card designs
const COLOR_PRESETS = [
  { id: 'classic', name: 'Classic', primary: '#1a1a2e', secondary: '#16213e', accent: '#0f3460', text: '#ffffff' },
  { id: 'ocean', name: 'Ocean Blue', primary: '#0077b6', secondary: '#00b4d8', accent: '#90e0ef', text: '#ffffff' },
  { id: 'forest', name: 'Forest', primary: '#2d6a4f', secondary: '#40916c', accent: '#52b788', text: '#ffffff' },
  { id: 'sunset', name: 'Sunset', primary: '#e63946', secondary: '#f77f00', accent: '#fcbf49', text: '#ffffff' },
  { id: 'royal', name: 'Royal Purple', primary: '#7209b7', secondary: '#560bad', accent: '#480ca8', text: '#ffffff' },
  { id: 'minimal', name: 'Minimal', primary: '#ffffff', secondary: '#f8f9fa', accent: '#e9ecef', text: '#212529' },
  { id: 'dark', name: 'Dark Mode', primary: '#212529', secondary: '#343a40', accent: '#495057', text: '#f8f9fa' },
  { id: 'rose', name: 'Rose Gold', primary: '#b76e79', secondary: '#c9a0a0', accent: '#e8c4c4', text: '#ffffff' },
];

const SOCIAL_ICONS = {
  linkedin: Linkedin,
  twitter: Twitter,
  instagram: Instagram,
  github: Github,
  facebook: Facebook,
};

const PrintPreviewPage = () => {
  const { cardId } = useParams();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const logoInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [cardData, setCardData] = useState(null);
  const [orientation, setOrientation] = useState('horizontal'); // 'horizontal' or 'vertical'
  const [selectedColor, setSelectedColor] = useState(COLOR_PRESETS[0]);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [template, setTemplate] = useState('professional'); // 'professional', 'modern', 'minimal'
  
  // Content toggles
  const [showFields, setShowFields] = useState({
    name: true,
    jobTitle: true,
    company: true,
    email: true,
    phone: true,
    website: false,
    address: false,
    qrCode: true,
    profilePhoto: true,
    companyLogo: true,
    socialLinks: false,
  });

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/cards/${cardId}`, { headers: getAuthHeaders() });
        setCardData(response.data);
        // Load company logo if saved
        if (response.data.company_logo) {
          setCompanyLogo(response.data.company_logo);
        }
      } catch (error) {
        toast.error('Card not found');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchCard();
  }, [cardId]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/upload/photo`, formDataUpload, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setCompanyLogo(response.data.url);
      
      // Save company logo to card
      await axios.put(`${API_URL}/api/cards/${cardId}`, { company_logo: response.data.url }, { headers: getAuthHeaders() });
      toast.success('Company logo uploaded');
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  const toggleField = (field) => {
    setShowFields(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Business Card - ${cardData?.name}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            @page { size: ${orientation === 'horizontal' ? '3.5in 2in' : '2in 3.5in'}; margin: 0; }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
            .card-container { width: ${orientation === 'horizontal' ? '3.5in' : '2in'}; height: ${orientation === 'horizontal' ? '2in' : '3.5in'}; }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const downloadAsImage = async () => {
    const printContent = printRef.current;
    if (!printContent) return;

    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(printContent, {
        scale: 3,
        useCORS: true,
        backgroundColor: null,
      });
      
      const link = document.createElement('a');
      link.download = `${cardData?.name?.replace(/\s+/g, '_')}_business_card.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Card downloaded as image');
    } catch (error) {
      toast.error('Failed to download card');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="loading-spinner" />
        </div>
      </Layout>
    );
  }

  const cardUrl = `${FRONTEND_URL}/card/${cardId}`;
  const primaryEmail = cardData?.emails?.[0]?.value || cardData?.email;
  const primaryPhone = cardData?.phones?.[0]?.value || cardData?.phone;
  const primaryJobTitle = cardData?.job_titles?.[0]?.value || cardData?.job_title;
  const primaryWebsite = cardData?.websites?.[0]?.value || cardData?.website;
  const primaryAddress = cardData?.addresses?.[0]?.value || cardData?.address;

  // Card dimensions
  const cardWidth = orientation === 'horizontal' ? 350 : 200;
  const cardHeight = orientation === 'horizontal' ? 200 : 350;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/cards/${cardId}/edit`)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold tracking-tight">Print Preview</h1>
            <p className="text-muted-foreground">Design and customize your physical NFC card</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadAsImage} className="gap-2" data-testid="download-card-btn">
              <Download className="w-4 h-4" />
              Download
            </Button>
            <Button onClick={handlePrint} className="gap-2" data-testid="print-card-btn">
              <Printer className="w-4 h-4" />
              Print
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Settings Panel */}
          <div className="xl:col-span-1 space-y-6">
            {/* Orientation */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Card Layout</CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs value={orientation} onValueChange={setOrientation}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="horizontal" className="gap-2" data-testid="orientation-horizontal">
                      <RectangleHorizontal className="w-4 h-4" />
                      Horizontal
                    </TabsTrigger>
                    <TabsTrigger value="vertical" className="gap-2" data-testid="orientation-vertical">
                      <RectangleVertical className="w-4 h-4" />
                      Vertical
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                <p className="text-xs text-muted-foreground mt-3">
                  Standard business card: {orientation === 'horizontal' ? '3.5" × 2"' : '2" × 3.5"'}
                </p>
              </CardContent>
            </Card>

            {/* Template Style */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Template Style</CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs value={template} onValueChange={setTemplate}>
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="professional" data-testid="template-professional">Pro</TabsTrigger>
                    <TabsTrigger value="modern" data-testid="template-modern">Modern</TabsTrigger>
                    <TabsTrigger value="minimal" data-testid="template-minimal">Minimal</TabsTrigger>
                  </TabsList>
                </Tabs>
              </CardContent>
            </Card>

            {/* Color Scheme */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Color Scheme
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-2">
                  {COLOR_PRESETS.map((preset) => (
                    <button
                      key={preset.id}
                      onClick={() => setSelectedColor(preset)}
                      className={`relative w-full aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        selectedColor.id === preset.id ? 'border-primary ring-2 ring-primary/30' : 'border-transparent hover:border-muted-foreground/30'
                      }`}
                      title={preset.name}
                      data-testid={`color-${preset.id}`}
                    >
                      <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${preset.primary} 0%, ${preset.secondary} 100%)` }} />
                      {selectedColor.id === preset.id && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-3 h-3 rounded-full bg-white shadow-md" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mt-3 text-center">{selectedColor.name}</p>
              </CardContent>
            </Card>

            {/* Company Logo Upload */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Camera className="w-5 h-5" />
                  Company Logo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div 
                    className="w-16 h-16 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center cursor-pointer hover:border-primary transition-colors overflow-hidden"
                    onClick={() => logoInputRef.current?.click()}
                    data-testid="upload-logo-btn"
                  >
                    {companyLogo ? (
                      <img src={companyLogo} alt="Company Logo" className="w-full h-full object-contain" />
                    ) : (
                      <Building2 className="w-6 h-6 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">Upload logo</p>
                    <p className="text-xs text-muted-foreground">PNG or JPG, max 5MB</p>
                  </div>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Content Toggles */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Display Fields</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { key: 'name', label: 'Name', icon: null },
                  { key: 'jobTitle', label: 'Job Title', icon: Briefcase },
                  { key: 'company', label: 'Company', icon: Building2 },
                  { key: 'email', label: 'Email', icon: Mail },
                  { key: 'phone', label: 'Phone', icon: Phone },
                  { key: 'website', label: 'Website', icon: Globe },
                  { key: 'address', label: 'Address', icon: MapPin },
                  { key: 'qrCode', label: 'QR Code', icon: null },
                  { key: 'profilePhoto', label: 'Profile Photo', icon: null },
                  { key: 'companyLogo', label: 'Company Logo', icon: null },
                  { key: 'socialLinks', label: 'Social Links', icon: null },
                ].map(({ key, label, icon: Icon }) => (
                  <div key={key} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
                      <Label htmlFor={`toggle-${key}`} className="text-sm cursor-pointer">{label}</Label>
                    </div>
                    <Switch
                      id={`toggle-${key}`}
                      checked={showFields[key]}
                      onCheckedChange={() => toggleField(key)}
                      data-testid={`toggle-${key}`}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Preview Panel */}
          <div className="xl:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Card Preview</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center gap-6 py-8">
                {/* Live Preview */}
                <div 
                  ref={printRef}
                  className="card-preview-container"
                  style={{
                    width: `${cardWidth}px`,
                    height: `${cardHeight}px`,
                  }}
                >
                  {/* Professional Template */}
                  {template === 'professional' && (
                    <div 
                      className="w-full h-full rounded-xl overflow-hidden shadow-2xl relative"
                      style={{
                        background: `linear-gradient(135deg, ${selectedColor.primary} 0%, ${selectedColor.secondary} 100%)`,
                        color: selectedColor.text,
                      }}
                    >
                      {orientation === 'horizontal' ? (
                        // Horizontal Professional
                        <div className="flex h-full">
                          {/* Left Section */}
                          <div className="flex-1 p-4 flex flex-col justify-between">
                            <div>
                              {showFields.companyLogo && companyLogo && (
                                <img src={companyLogo} alt="Logo" className="h-6 w-auto mb-2 object-contain" style={{ filter: selectedColor.id === 'minimal' ? 'none' : 'brightness(0) invert(1)' }} />
                              )}
                              {showFields.name && (
                                <h2 className="font-bold text-lg leading-tight">{cardData?.name}</h2>
                              )}
                              {showFields.jobTitle && primaryJobTitle && (
                                <p className="text-xs opacity-90 mt-0.5">{primaryJobTitle}</p>
                              )}
                              {showFields.company && cardData?.company && (
                                <p className="text-xs opacity-75">{cardData.company}</p>
                              )}
                            </div>
                            <div className="space-y-1 text-xs">
                              {showFields.email && primaryEmail && (
                                <div className="flex items-center gap-1.5 opacity-90">
                                  <Mail className="w-3 h-3" />
                                  <span className="truncate">{primaryEmail}</span>
                                </div>
                              )}
                              {showFields.phone && primaryPhone && (
                                <div className="flex items-center gap-1.5 opacity-90">
                                  <Phone className="w-3 h-3" />
                                  <span>{primaryPhone}</span>
                                </div>
                              )}
                              {showFields.website && primaryWebsite && (
                                <div className="flex items-center gap-1.5 opacity-90">
                                  <Globe className="w-3 h-3" />
                                  <span className="truncate">{primaryWebsite}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Right Section */}
                          <div className="w-24 flex flex-col items-center justify-center p-2" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                            {showFields.profilePhoto && cardData?.profile_photo ? (
                              <img src={cardData.profile_photo} alt="Profile" className="w-14 h-14 rounded-full object-cover border-2 border-white/30 mb-2" />
                            ) : showFields.profilePhoto ? (
                              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mb-2">
                                <span className="text-xl font-bold">{cardData?.name?.charAt(0)}</span>
                              </div>
                            ) : null}
                            {showFields.qrCode && (
                              <div className="bg-white p-1 rounded">
                                <QRCodeSVG value={cardUrl} size={50} level="M" />
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        // Vertical Professional
                        <div className="flex flex-col h-full p-4">
                          <div className="flex justify-between items-start mb-3">
                            {showFields.companyLogo && companyLogo && (
                              <img src={companyLogo} alt="Logo" className="h-5 w-auto object-contain" style={{ filter: selectedColor.id === 'minimal' ? 'none' : 'brightness(0) invert(1)' }} />
                            )}
                            {showFields.qrCode && (
                              <div className="bg-white p-1 rounded">
                                <QRCodeSVG value={cardUrl} size={35} level="M" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 flex flex-col items-center justify-center text-center">
                            {showFields.profilePhoto && cardData?.profile_photo ? (
                              <img src={cardData.profile_photo} alt="Profile" className="w-16 h-16 rounded-full object-cover border-2 border-white/30 mb-2" />
                            ) : showFields.profilePhoto ? (
                              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mb-2">
                                <span className="text-2xl font-bold">{cardData?.name?.charAt(0)}</span>
                              </div>
                            ) : null}
                            {showFields.name && (
                              <h2 className="font-bold text-base leading-tight">{cardData?.name}</h2>
                            )}
                            {showFields.jobTitle && primaryJobTitle && (
                              <p className="text-xs opacity-90 mt-0.5">{primaryJobTitle}</p>
                            )}
                            {showFields.company && cardData?.company && (
                              <p className="text-xs opacity-75">{cardData.company}</p>
                            )}
                          </div>
                          <div className="space-y-1 text-xs mt-auto">
                            {showFields.email && primaryEmail && (
                              <div className="flex items-center gap-1.5 opacity-90">
                                <Mail className="w-3 h-3" />
                                <span className="truncate">{primaryEmail}</span>
                              </div>
                            )}
                            {showFields.phone && primaryPhone && (
                              <div className="flex items-center gap-1.5 opacity-90">
                                <Phone className="w-3 h-3" />
                                <span>{primaryPhone}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Modern Template */}
                  {template === 'modern' && (
                    <div 
                      className="w-full h-full rounded-xl overflow-hidden shadow-2xl relative"
                      style={{ backgroundColor: selectedColor.primary, color: selectedColor.text }}
                    >
                      {/* Decorative Elements */}
                      <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-20" style={{ background: selectedColor.accent, transform: 'translate(30%, -30%)' }} />
                      <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full opacity-15" style={{ background: selectedColor.secondary, transform: 'translate(-30%, 30%)' }} />
                      
                      {orientation === 'horizontal' ? (
                        // Horizontal Modern
                        <div className="relative z-10 flex h-full p-4">
                          <div className="flex-1 flex flex-col justify-between">
                            <div>
                              {showFields.companyLogo && companyLogo && (
                                <img src={companyLogo} alt="Logo" className="h-5 w-auto mb-2 object-contain" style={{ filter: selectedColor.id === 'minimal' ? 'none' : 'brightness(0) invert(1)' }} />
                              )}
                              <div className="flex items-center gap-3">
                                {showFields.profilePhoto && cardData?.profile_photo && (
                                  <img src={cardData.profile_photo} alt="Profile" className="w-12 h-12 rounded-lg object-cover" />
                                )}
                                <div>
                                  {showFields.name && (
                                    <h2 className="font-bold text-lg leading-tight">{cardData?.name}</h2>
                                  )}
                                  {showFields.jobTitle && primaryJobTitle && (
                                    <p className="text-xs opacity-80">{primaryJobTitle}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-4 text-xs">
                              {showFields.email && primaryEmail && (
                                <div className="opacity-80 truncate max-w-[120px]">{primaryEmail}</div>
                              )}
                              {showFields.phone && primaryPhone && (
                                <div className="opacity-80">{primaryPhone}</div>
                              )}
                            </div>
                          </div>
                          {showFields.qrCode && (
                            <div className="flex items-center">
                              <div className="bg-white p-1.5 rounded-lg">
                                <QRCodeSVG value={cardUrl} size={55} level="M" />
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        // Vertical Modern
                        <div className="relative z-10 flex flex-col h-full p-4">
                          {showFields.companyLogo && companyLogo && (
                            <img src={companyLogo} alt="Logo" className="h-5 w-auto mb-3 object-contain self-start" style={{ filter: selectedColor.id === 'minimal' ? 'none' : 'brightness(0) invert(1)' }} />
                          )}
                          <div className="flex-1 flex flex-col items-center justify-center">
                            {showFields.profilePhoto && cardData?.profile_photo && (
                              <img src={cardData.profile_photo} alt="Profile" className="w-14 h-14 rounded-xl object-cover mb-2" />
                            )}
                            {showFields.name && (
                              <h2 className="font-bold text-base text-center leading-tight">{cardData?.name}</h2>
                            )}
                            {showFields.jobTitle && primaryJobTitle && (
                              <p className="text-xs opacity-80 text-center">{primaryJobTitle}</p>
                            )}
                            {showFields.company && cardData?.company && (
                              <p className="text-xs opacity-60 text-center">{cardData.company}</p>
                            )}
                          </div>
                          <div className="flex justify-between items-end">
                            <div className="text-xs space-y-0.5">
                              {showFields.email && primaryEmail && <div className="opacity-80 truncate max-w-[100px]">{primaryEmail}</div>}
                              {showFields.phone && primaryPhone && <div className="opacity-80">{primaryPhone}</div>}
                            </div>
                            {showFields.qrCode && (
                              <div className="bg-white p-1 rounded">
                                <QRCodeSVG value={cardUrl} size={40} level="M" />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Minimal Template */}
                  {template === 'minimal' && (
                    <div 
                      className="w-full h-full rounded-xl overflow-hidden shadow-2xl border"
                      style={{ 
                        backgroundColor: selectedColor.id === 'minimal' ? '#ffffff' : selectedColor.primary, 
                        color: selectedColor.text,
                        borderColor: selectedColor.id === 'minimal' ? '#e5e7eb' : 'transparent'
                      }}
                    >
                      {orientation === 'horizontal' ? (
                        // Horizontal Minimal
                        <div className="flex h-full">
                          <div className="w-2" style={{ backgroundColor: selectedColor.accent }} />
                          <div className="flex-1 p-4 flex flex-col justify-between">
                            <div className="flex justify-between items-start">
                              <div>
                                {showFields.name && (
                                  <h2 className="font-semibold text-lg">{cardData?.name}</h2>
                                )}
                                {showFields.jobTitle && primaryJobTitle && (
                                  <p className="text-xs opacity-70">{primaryJobTitle}</p>
                                )}
                                {showFields.company && cardData?.company && (
                                  <p className="text-xs opacity-50 font-medium mt-1">{cardData.company}</p>
                                )}
                              </div>
                              {showFields.companyLogo && companyLogo && (
                                <img src={companyLogo} alt="Logo" className="h-8 w-auto object-contain" />
                              )}
                            </div>
                            <div className="flex justify-between items-end">
                              <div className="space-y-0.5 text-xs">
                                {showFields.email && primaryEmail && (
                                  <div className="opacity-70">{primaryEmail}</div>
                                )}
                                {showFields.phone && primaryPhone && (
                                  <div className="opacity-70">{primaryPhone}</div>
                                )}
                                {showFields.website && primaryWebsite && (
                                  <div className="opacity-70">{primaryWebsite}</div>
                                )}
                              </div>
                              {showFields.qrCode && (
                                <div className="p-1 rounded" style={{ backgroundColor: selectedColor.id === 'minimal' ? '#f3f4f6' : 'rgba(255,255,255,0.9)' }}>
                                  <QRCodeSVG value={cardUrl} size={45} level="M" fgColor={selectedColor.id === 'minimal' ? '#374151' : selectedColor.primary} />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        // Vertical Minimal
                        <div className="flex flex-col h-full">
                          <div className="h-1.5" style={{ backgroundColor: selectedColor.accent }} />
                          <div className="flex-1 p-4 flex flex-col">
                            <div className="flex justify-between items-start">
                              {showFields.companyLogo && companyLogo && (
                                <img src={companyLogo} alt="Logo" className="h-6 w-auto object-contain" />
                              )}
                              {showFields.qrCode && (
                                <div className="p-1 rounded" style={{ backgroundColor: selectedColor.id === 'minimal' ? '#f3f4f6' : 'rgba(255,255,255,0.9)' }}>
                                  <QRCodeSVG value={cardUrl} size={35} level="M" fgColor={selectedColor.id === 'minimal' ? '#374151' : selectedColor.primary} />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 flex flex-col justify-center">
                              {showFields.name && (
                                <h2 className="font-semibold text-base">{cardData?.name}</h2>
                              )}
                              {showFields.jobTitle && primaryJobTitle && (
                                <p className="text-xs opacity-70">{primaryJobTitle}</p>
                              )}
                              {showFields.company && cardData?.company && (
                                <p className="text-xs opacity-50 font-medium mt-1">{cardData.company}</p>
                              )}
                            </div>
                            <div className="space-y-0.5 text-xs mt-auto">
                              {showFields.email && primaryEmail && (
                                <div className="opacity-70">{primaryEmail}</div>
                              )}
                              {showFields.phone && primaryPhone && (
                                <div className="opacity-70">{primaryPhone}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <Separator />

                {/* Print Info */}
                <div className="text-center space-y-2 max-w-md">
                  <p className="text-sm text-muted-foreground">
                    This preview shows how your physical NFC business card will look.
                    Standard business card size: {orientation === 'horizontal' ? '3.5" × 2"' : '2" × 3.5"'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    For best results, use high-quality cardstock paper when printing.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PrintPreviewPage;
